import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
    if (typeof detail === "object" && detail.msg) return detail.msg;
    return String(detail);
};

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/auth/me`, { credentials: "include" })
            .then((res) => (res.ok ? res.json() : null))
            .then((user) => {
                if (user?.role === "admin") navigate("/admin", { replace: true });
            })
            .catch(() => {});
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setError("");
        if (!email.trim() || !email.includes("@")) {
            setError("Enter a valid admin email address.");
            return;
        }
        if (!password) {
            setError("Enter your password.");
            return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API}/api/auth/login`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password, remember }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(formatError(body.detail));
            }
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(err.message || "Sign in failed. Please try again.");
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen bg-[#F8FAFC] font-adminSans text-[#0F172A] lg:grid-cols-2" data-testid="admin-login-page">
            <div className="relative hidden overflow-hidden bg-[#0B1120] lg:block">
                <img
                    src="https://images.pexels.com/photos/12623752/pexels-photo-12623752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Modern architectural forms in dramatic light"
                    className="absolute inset-0 h-full w-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-[#0B1120]/65" />
                <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-4">
                        <span className="flex h-14 w-14 items-center justify-center rounded-md bg-[#D4AF37] font-adminDisplay text-sm font-bold tracking-[0.12em] text-[#0B1120]">AGL</span>
                        <span>
                            <span className="block font-adminDisplay text-xl font-bold uppercase tracking-[0.12em]">Afrinexa Global</span>
                            <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.32em] text-white/50">Secure Operations</span>
                        </span>
                    </div>
                    <div className="max-w-xl">
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">Admin Portal</p>
                        <h2 className="mt-5 font-adminDisplay text-5xl font-bold leading-[1.02] tracking-tight">
                            Controlled access for the Afrinexa operations team.
                        </h2>
                        <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
                            Recruitment, candidate, job and system administration protected by server-side authentication, role-based access and audit logging.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/45">
                        <ShieldCheck size={15} className="text-[#D4AF37]" />
                        Sessions expire automatically and sensitive actions are audited.
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center px-6 py-12 sm:px-10">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex items-center gap-3.5 lg:hidden">
                        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0B1120] font-adminDisplay text-xs font-bold tracking-[0.12em] text-[#D4AF37]">AGL</span>
                        <span className="leading-tight">
                            <span className="block font-adminDisplay text-lg font-bold uppercase tracking-[0.12em]">Afrinexa Global</span>
                            <span className="block text-[9px] font-bold uppercase tracking-[0.3em] text-[#64748B]">Admin Portal</span>
                        </span>
                    </div>

                    <form
                        data-testid="admin-login-form"
                        onSubmit={handleSubmit}
                        className="rounded-lg border border-[#E2E8F0] bg-white p-8 shadow-2xl sm:p-10"
                    >
                        <h1 className="font-adminDisplay text-4xl font-bold tracking-tight">Admin Portal</h1>
                        <p className="mt-3 text-sm leading-relaxed text-[#64748B]">
                            Sign in with an authorized administrator account.
                        </p>

                        <div className="mt-8 space-y-5">
                            <div>
                                <label htmlFor="admin-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">
                                    Email address
                                </label>
                                <input
                                    id="admin-email"
                                    data-testid="admin-email-input"
                                    type="email"
                                    required
                                    autoComplete="username"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-4 py-3.5 text-sm outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30"
                                    placeholder="you@afrinexaglobal.org"
                                />
                            </div>
                            <div>
                                <div className="mb-2 flex items-center justify-between">
                                    <label htmlFor="admin-password" className="block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">
                                        Password
                                    </label>
                                    <button
                                        type="button"
                                        data-testid="toggle-admin-password"
                                        onClick={() => setShowPassword((value) => !value)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#64748B] transition-colors hover:text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                                    >
                                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                        {showPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                                <input
                                    id="admin-password"
                                    data-testid="admin-password-input"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-4 py-3.5 text-sm outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30"
                                    placeholder="Enter your password"
                                />
                            </div>
                        </div>

                        <div className="mt-5 flex items-center justify-between gap-4">
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#64748B]">
                                <input
                                    type="checkbox"
                                    data-testid="admin-remember-me"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                    className="h-4 w-4 rounded border-[#CBD5E1] text-[#0B1120] focus:ring-[#D4AF37]"
                                />
                                Remember me
                            </label>
                            <Link to="/admin/forgot-password" data-testid="admin-forgot-password-link" className="text-xs font-bold uppercase tracking-[0.12em] text-[#0B1120] transition-colors hover:text-[#D4AF37]">
                                Forgot password?
                            </Link>
                        </div>

                        {error && (
                            <p data-testid="admin-login-error" className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            data-testid="admin-login-button"
                            disabled={busy}
                            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0B1120] px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:bg-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            {busy ? (
                                <>
                                    <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                                    Signing in securely…
                                </>
                            ) : (
                                <>
                                    <Lock size={15} strokeWidth={1.5} />
                                    Sign In
                                </>
                            )}
                        </button>
                    </form>

                    <Link
                        to="/"
                        data-testid="admin-back-home-link"
                        className="mt-8 inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#64748B] transition-colors hover:text-[#D4AF37]"
                    >
                        ← Back to website
                    </Link>
                </div>
            </div>
        </div>
    );
}
